import type {
    CreateExtArgs,
    CreateExtensionArgument,
    DmApis,
    Extension,
    ExtensionAPI,
    ResourceLoaderArgs,
    ResourceLoaderDefinition
} from '@wix/document-manager-core'
import _ from 'lodash'
import type {RMApi} from './rendererModel'
import type {Reference, SchemaCollection} from './csave/serverProtocolTypes'
import type {BuilderEditorClientSpecMapEntry, ClientSpecMap} from '@wix/document-services-types'
import {RESOURCE_TYPES} from '../constants/constants'
import {hasBuilderComponents} from '../utils/appUtils'
import {registerSchemaCollection} from '../utils/schemaUtils'
import type {DistributorExtensionAPI} from './distributor/distributor'
import type {ManifestUpdateMessage} from './distributor/messages'
import {ReportableError} from '@wix/document-manager-utils'

const INTERACTIONS = {
    LOAD_SCHEMAS: 'load-schemas',
    REGISTER_SCHEMAS: 'register-schemas',
    SCHEMA_RESOURCES_LOADING_FAILED: 'schema-resources-loading-failed'
}
export const MANIFEST_UPDATE_MESSAGE: ManifestUpdateMessage['messageType'] = 'MANIFEST_UPDATE'

export interface SchemaResourceLoaderExtensionAPI extends ExtensionAPI {
    schemaResourceLoader: {
        sendStoredReferencesToViewer(): void
    }
}

export interface SchemaCollectionServer {
    getSchemaCollectionsByAppIds(appDefinitionIds: string[], overrideIds?: string[]): Promise<SchemaCollection[]>
}

type TypeToReferencesMap = Record<string, Reference[]>
type NamespaceToReferencesMap = Record<string, TypeToReferencesMap>
export type SchemaCollectionReferencesMap = Record<string, NamespaceToReferencesMap>

const EVENTS = {
    SCHEMA_RESOURCE_LOADER: {
        DYNAMIC_REFERENCES_UPDATE_EVENT: 'DYNAMIC_REFERENCES_UPDATE_EVENT'
    }
}

const mergeReferencesIntoMap = (
    referencesMap: SchemaCollectionReferencesMap,
    appId: string,
    references: NamespaceToReferencesMap | undefined
) => {
    if (!references) return

    if (!referencesMap[appId]) {
        referencesMap[appId] = {}
    }

    for (const [namespace, typeToReferencesMap] of Object.entries(references)) {
        if (!_.isEmpty(typeToReferencesMap)) {
            if (!referencesMap[appId][namespace]) {
                referencesMap[appId][namespace] = {}
            }
            Object.assign(referencesMap[appId][namespace], typeToReferencesMap)
        }
    }
}

export const createExtension = ({
    environmentContext,
    logger,
    experimentInstance
}: CreateExtensionArgument): Extension => {
    let storedReferences: SchemaCollectionReferencesMap = {}
    let viewerIsInitialized = false

    const createResourceLoader = (args: ResourceLoaderArgs): ResourceLoaderDefinition[] => {
        const {dal, pointers, eventEmitter, coreConfig} = args
        const {rendererModel} = args.extensionAPI as RMApi

        const load = async (appDefIds: string[]) => {
            if (!appDefIds.length) {
                return
            }

            const overrideIds: string[] = []
            for (const appDefId of appDefIds) {
                const overrideId = dal.get(pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefId))?.appFields
                    ?.overrideId
                if (overrideId) overrideIds.push(overrideId)
            }

            try {
                logger.interactionStarted(INTERACTIONS.LOAD_SCHEMAS)

                const allSchemaCollections: SchemaCollection[] =
                    await environmentContext.serverFacade.getSchemaCollectionsByAppIds(
                        appDefIds,
                        overrideIds.length > 0 ? overrideIds : undefined
                    )

                logger.interactionStarted(INTERACTIONS.REGISTER_SCHEMAS)

                const referencesMap: SchemaCollectionReferencesMap = {}
                for (const schemaCollection of allSchemaCollections) {
                    const {innerElements, appId} = schemaCollection
                    const topLevelComponentReferences = registerSchemaCollection(
                        coreConfig.schemaService,
                        schemaCollection
                    )
                    mergeReferencesIntoMap(referencesMap, appId!, topLevelComponentReferences)
                    if (innerElements) {
                        for (const innerElement of innerElements) {
                            const innerElementReferences = registerSchemaCollection(
                                coreConfig.schemaService,
                                innerElement
                            )
                            mergeReferencesIntoMap(referencesMap, appId!, innerElementReferences)
                        }
                    }
                }
                if (!_.isEmpty(referencesMap)) {
                    if (viewerIsInitialized) {
                        eventEmitter.emit(EVENTS.SCHEMA_RESOURCE_LOADER.DYNAMIC_REFERENCES_UPDATE_EVENT, referencesMap)
                    } else if (coreConfig.withoutViewer) {
                        // no one needs references map
                    } else {
                        storedReferences = referencesMap
                    }
                }

                logger.interactionEnded(INTERACTIONS.REGISTER_SCHEMAS)
                logger.interactionEnded(INTERACTIONS.LOAD_SCHEMAS)
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: INTERACTIONS.SCHEMA_RESOURCES_LOADING_FAILED,
                        message: `Resources loading failed for appIds: ${appDefIds}`,
                        tags: {
                            appDefIds
                        },
                        extras: {
                            originalError: error
                        }
                    })
                )
                throw error
            }
        }

        const getAvailableResources = () => {
            const clientSpecMap = rendererModel.getClientSpecMap() as ClientSpecMap

            if (!clientSpecMap) {
                return []
            }

            return _.uniq(
                Object.values(clientSpecMap)
                    .filter(application => hasBuilderComponents(application as BuilderEditorClientSpecMapEntry))
                    .map(appSpec => (appSpec as BuilderEditorClientSpecMapEntry).appDefinitionId!)
                    .filter(Boolean)
            )
        }

        const loadAll = async () => {
            await load(getAvailableResources())
        }

        const initAvailableResources = async () => {
            if (
                !experimentInstance.isOpen('dm_schemaResourceLoader') &&
                !pointers.rendererModel.isBuilderComponentModel()
            ) {
                return
            }

            await loadAll()
        }

        return [
            {
                resourceType: RESOURCE_TYPES.SCHEMA,
                load,
                loadAll,
                getAvailableResources,
                initAvailableResources
            }
        ]
    }

    const createExtensionAPI = ({eventEmitter}: CreateExtArgs): SchemaResourceLoaderExtensionAPI => {
        return {
            schemaResourceLoader: {
                sendStoredReferencesToViewer: () => {
                    if (!_.isEmpty(storedReferences)) {
                        eventEmitter.emit(
                            EVENTS.SCHEMA_RESOURCE_LOADER.DYNAMIC_REFERENCES_UPDATE_EVENT,
                            storedReferences
                        )
                    }
                    viewerIsInitialized = true
                    storedReferences = {}
                }
            }
        }
    }

    const initializeChannelSubscriptions = async ({extensionAPI, resourceLoader}: DmApis) => {
        const {distributor, rendererModel} = extensionAPI as DistributorExtensionAPI & RMApi
        distributor.subscribeToMessage<ManifestUpdateMessage>(MANIFEST_UPDATE_MESSAGE, async ({data}) => {
            if (!data?.appId) {
                throw new Error('appId should be provided')
            }
            await rendererModel.refreshClientSpecMap()
            await resourceLoader.load(RESOURCE_TYPES.SCHEMA, [data.appId])
        })
    }

    return {
        name: 'schemaResourceLoader',
        dependencies: new Set(['schema', 'rendererModel']),
        createResourceLoader,
        createExtensionAPI,
        EVENTS,
        initializeChannelSubscriptions
    }
}
